'use client';

import { storyblokEditable } from '@storyblok/react';
import { clsx } from 'clsx';
import { FC, RefObject, useEffect, useRef, useState } from 'react';
import type { InfoBlockStoryblok } from 'storyblok-types';
import { Container } from 'atoms/Container';
import { StoryBlokLink } from 'atoms/StoryBlokLink';
import { H1, Span } from 'atoms/Typography';
import { Accordion } from 'pages/StoryBlok/SeoAccordeon/Accordion/Accordion';
import { useTranslations } from 'modules/i18n/useTranslations';
import styles from './styles.module.css';

export const InfoBlock: FC<{
  blok: InfoBlockStoryblok;
}> = ({ blok }) => {
  const t = useTranslations();
  const descriptionRef: RefObject<HTMLElement | null> = useRef(null);
  const [expanded, setExpanded] = useState<boolean>(false);
  const [isMultiline, setIsMultiline] = useState(false);
  const isInfoBlockVisible = Boolean(
    blok.Headline || blok.seoFooterIntroduction || blok.anchorLinks?.length,
  ); // check if at least one field is filled, then the infoBlock will be rendered

  useEffect(() => {
    const checkMultiline = () => {
      const descriptionElement = descriptionRef?.current;

      const flag = Boolean(
        descriptionElement &&
          descriptionElement.scrollWidth > descriptionElement.clientWidth,
      );

      setIsMultiline(flag);
    };

    checkMultiline();

    window.addEventListener('resize', checkMultiline);

    return () => {
      window.removeEventListener('resize', checkMultiline);
    };
  }, [blok.seoFooterIntroduction, descriptionRef.current]);

  const summaryContent = expanded
    ? t('faq.accordion-button-expanded-text')
    : t('faq.accordion-button-collapsed-text');

  const descriptionClasses = expanded
    ? clsx(styles.description, styles.descriptionExpanded)
    : clsx(styles.description, styles.descriptionMultiline);

  return !blok || !isInfoBlockVisible ? null : (
    <Container>
      <div className={styles.container} {...storyblokEditable(blok)}>
        <H1 className={styles.title}>{blok.Headline}</H1>
        <Span ref={descriptionRef} className={descriptionClasses}>
          {blok.seoFooterIntroduction}
        </Span>
        {((blok.anchorLinks &&
          blok.anchorLinks.length > 0 &&
          blok.seoFooterIntroduction) ||
          isMultiline) && (
          <Accordion
            summaryContent={summaryContent}
            classes={{
              content: styles.accordionSummaryContent,
              summaryRoot: styles.accordionSummaryRoot,
              icon: styles.accordionIconClass,
              details: styles.accordionDetails,
            }}
            onChange={(e, exp) => {
              setExpanded(exp);
            }}
          >
            <div className={styles.linksContainer}>
              {blok.anchorLinks && blok.anchorLinks.length > 0 && (
                <Span className={styles.linksTitle}>{t('faq.title-list')}</Span>
              )}
              <ul className={styles.linksList}>
                {blok.anchorLinks?.map(({ _uid, link, text }) => {
                  if (link) {
                    return (
                      <li key={_uid}>
                        <StoryBlokLink link={link} className={styles.linkItem}>
                          {text}
                        </StoryBlokLink>
                      </li>
                    );
                  }
                })}
              </ul>
            </div>
          </Accordion>
        )}
      </div>
    </Container>
  );
};
